import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  setCloseDeleteDialog,
  setOpenDeleteDialog,
} from "./orderLabelingSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";

const OrderLabelingArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const deletItemInsex = useAppSelector(
    (state) => state.orderLabeling.deletItemInsex,
  );
  const ItemList = useAppSelector((state) => state.item.ItemList) || [];
  const deleteDialogOpen = useAppSelector(
    (state) => state.orderLabeling.deleteDialogOpen,
  );
  const isViewDialog = dialogName?.startsWith("view-order-labeling-");
  const fieldName = fieldArrayRenderProps.name;

  const calculateTotal = (weight: number, rate: number) => {
    return (weight || 0) * (rate || 0);
  };

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        fieldArrayRenderProps.onRemove({ index: index });
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
      }
      dispatch(setCloseDeleteDialog());
    },
    [fieldArrayRenderProps, dispatch],
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        ItemName: "",
        Weight: 0,
        Pcs: 0,
        CustomerRate: 0,
        Total: 0,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(setOpenDeleteDialog(index));
  };

  const updateWeightTotals = () => {
    const orderLabeling =
      fieldArrayRenderProps.formRenderProps.valueGetter("ORDERLabeling") || [];
    const grossWeight = orderLabeling.reduce(
      (sum: number, item: any) => sum + (item.Weight || 0),
      0,
    );
    const lessWeight =
      fieldArrayRenderProps.formRenderProps.valueGetter("LessWeight") || 0;
    const netWeight = grossWeight - lessWeight;

    fieldArrayRenderProps.formRenderProps.onChange("GrossWeight", {
      value: grossWeight,
    });
    fieldArrayRenderProps.formRenderProps.onChange("NetWeight", {
      value: netWeight,
    });
  };

  return (
    <>
      <CustomAppDialog open={deleteDialogOpen}>
        <>
          <Typography.h5>{"Delete Label Item"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>
                Are you sure you want to delete this label item?
              </Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={setCloseDeleteDialog}
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => handleDeleteAction(deletItemInsex)}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>

      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Label Items Detail
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          {!isViewDialog && (
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Label Item
            </Button>
          )}
        </GridLayoutItem>
      </GridLayout>

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: isViewDialog ? "50vh" : "30vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: "100%" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Product Name</th>
                  <th style={{ width: "15%" }}>Weight (gm)</th>
                  <th style={{ width: "10%" }}>Pcs</th>
                  <th style={{ width: "20%" }}>Customer Rate (₹)</th>
                  <th style={{ width: "15%" }}>Total (₹)</th>
                  <th style={{ width: "5%", position: "sticky" }}></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (labelItem: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.ItemID`}
                            placeholder="Enter Product Name"
                            component={FormSelectionField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                            options={ItemList.map((item: any) => {
                              return {
                                value: item.id,
                                label: item.item_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Weight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                            onChange={(e: any) => {
                              const weight = e.value;
                              const rate =
                                fieldArrayRenderProps.formRenderProps.valueGetter(
                                  `${fieldName}.${index}.CustomerRate`,
                                );
                              const total = calculateTotal(weight, rate);
                              fieldArrayRenderProps.formRenderProps.onChange(
                                `${fieldName}.${index}.Total`,
                                { value: total },
                              );
                              updateWeightTotals();
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Pcs`}
                            placeholder="0"
                            format="n0"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.CustomerRate`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            disabled={isViewDialog}
                            onChange={(e: any) => {
                              const rate = e.value;
                              const weight =
                                fieldArrayRenderProps.formRenderProps.valueGetter(
                                  `${fieldName}.${index}.Weight`,
                                );
                              const total = calculateTotal(weight, rate);
                              fieldArrayRenderProps.formRenderProps.onChange(
                                `${fieldName}.${index}.Total`,
                                { value: total },
                              );
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Total`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            disabled={true}
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            style={{
                              position: "absolute",
                              height: 32,
                              width: 32,
                              right: 15,
                              pointerEvents: "all",
                            }}
                            onClick={() => handleOpenDeleteDialog(index)}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
};

export default OrderLabelingArray;
